import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, CheckCircle2, RotateCcw, X, Satellite, Radio } from "lucide-react";
import { Page, Panel, Field, SearchInput } from "../lib/ui";
import { api, can, today, humanDate, normalizePlateTR, localDay, localToday } from "../lib/api";

const EMPTY = {
  utts: { assembly_code: "", plate: "", date: today(), note: "" },
  tracker: { plate: "", chassis_no: "", customer_id: null, customer_name: "", serial_nos: [""], date: today(), description: "" },
};

function SerialList({ values, onChange }) {
  const set = (i, v) => onChange(values.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="serial-list" data-testid="tracker-serials">
      <span className="serial-label">Cihaz seri numaraları</span>
      {values.map((v, i) => (
        <div key={i} className="serial-row">
          <input data-testid={`tracker-serial-${i}`} value={v} placeholder={`Seri no ${i + 1}`}
                 onChange={(e) => set(i, e.target.value.toUpperCase())} />
          {values.length > 1 && (
            <button type="button" className="row-icon tone-danger" data-testid={`tracker-serial-remove-${i}`}
                    onClick={() => onChange(values.filter((_, idx) => idx !== i))}><X size={13} /></button>
          )}
        </div>
      ))}
      <button type="button" className="text-button" data-testid="tracker-serial-add" onClick={() => onChange([...values, ""])}>
        <Plus size={13} /> Seri no ekle
      </button>
    </div>
  );
}

function InstallForm({ kind, initial, customers, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY[kind]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const plateBlur = (e) => set({ plate: normalizePlateTR(e.target.value) });
  const [codeWarn, setCodeWarn] = useState("");
  const checkCode = async () => {
    const code = (form.assembly_code || "").trim();
    if (!code) return;
    const { data } = await api.get("/installs/utts/check-code", { params: { code, exclude_id: initial?.id || "" } }).catch(() => ({ data: {} }));
    setCodeWarn(data.exists ? data.message : "");
  };
  const submit = async (e) => {
    e.preventDefault();
    if (codeWarn) return setError(codeWarn);
    setError(""); setBusy(true);
    try {
      if (initial?.id) await api.put(`/installs/${kind}/${initial.id}`, form);
      else await api.post(`/installs/${kind}`, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };
  return (
    <form className="install-form" onSubmit={submit} data-testid={`${kind}-form`}>
      {kind === "utts" ? (
        <div className="form-grid three">
          <Field testid="utts-code-input" label="Montaj kodu" required value={form.assembly_code}
                 onChange={(e) => { set({ assembly_code: e.target.value.toUpperCase() }); setCodeWarn(""); }}
                 onBlur={checkCode} placeholder="UTTS-0001" />
          <Field testid="utts-plate-input" label="Plaka" required value={form.plate} placeholder="35 SS 3535"
                 onChange={(e) => set({ plate: e.target.value.toUpperCase() })} onBlur={plateBlur} />
          <Field testid="utts-date-input" label="Montaj tarihi" type="date" required value={form.date}
                 onChange={(e) => set({ date: e.target.value })} />
          {codeWarn && <div className="form-error" data-testid="utts-code-warning" style={{ gridColumn: "1 / -1" }}>{codeWarn}</div>}
        </div>
      ) : (
        <>
          <div className="form-grid three">
            <Field testid="tracker-plate-input" label="Plaka" value={form.plate} placeholder="35 SS 3535"
                   onChange={(e) => set({ plate: e.target.value.toUpperCase() })} onBlur={plateBlur} />
            <Field testid="tracker-chassis-input" label="Şase no" value={form.chassis_no}
                   onChange={(e) => set({ chassis_no: e.target.value.toUpperCase() })} />
            <Field testid="tracker-date-input" label="Montaj tarihi" type="date" required value={form.date}
                   onChange={(e) => set({ date: e.target.value })} />
          </div>
          <div className="form-grid two">
            <Field testid="tracker-customer-input" label="Müşteri (not)" value={form.customer_name}
                   hint="Sadece not olarak saklanır, cariye işlenmez"
                   onChange={(e) => set({ customer_name: e.target.value, customer_id: null })} placeholder="Müşteri adı" />
            <SerialList values={form.serial_nos?.length ? form.serial_nos : [""]} onChange={(v) => set({ serial_nos: v })} />
          </div>
          <label className="field">
            <span>Açıklama</span>
            <textarea data-testid="tracker-desc-input" rows="2" value={form.description}
                      onChange={(e) => set({ description: e.target.value })} />
          </label>
        </>
      )}
      {error && <div className="form-error" data-testid="install-form-error">{error}</div>}
      <div className="button-row">
        <button type="button" className="secondary-button" onClick={onCancel} data-testid="install-cancel">Vazgeç</button>
        <button type="submit" className="primary-button" disabled={busy} data-testid="install-save">
          {busy ? "Kaydediliyor…" : initial?.id ? "Güncelle" : "Kaydet"}
        </button>
      </div>
    </form>
  );
}

function InstallRow({ kind, r, selected, onToggle, onEdit, onDelete, canManage, mine }) {
  const editable = canManage || (mine && !r.invoiced);
  return (
    <tr className={r.invoiced ? "row-invoiced" : ""} data-testid={`${kind}-row-${r.id}`}>
      {canManage && (
        <td><input type="checkbox" checked={selected} onChange={onToggle} data-testid={`${kind}-select-${r.id}`} /></td>
      )}
      <td>{humanDate(r.date)}</td>
      {kind === "utts" ? (
        <><td><strong>{r.assembly_code}</strong></td><td>{r.plate}</td><td>{r.created_by_name || "—"}</td></>
      ) : (
        <>
          <td><strong>{r.plate || "—"}</strong><small>{r.chassis_no}</small></td>
          <td>{r.customer_name || "—"}</td>
          <td>{(r.serial_nos || []).map((s) => <span key={s} className="serial-chip">{s}</span>)}</td>
          <td className="wrap-cell">{r.description || "—"}</td>
        </>
      )}
      <td>
        <span className={`status-pill ${r.invoiced ? "paid" : "pending"}`} data-testid={`${kind}-status-${r.id}`}>
          {r.invoiced ? `Faturalandı${r.invoice_no ? ` · ${r.invoice_no}` : ""}` : "Faturalanmadı"}
        </span>
      </td>
      <td className="amount actions-cell">
        {editable && (
          <>
            <button className="row-icon tone-muted" title="Düzenle" data-testid={`${kind}-edit-${r.id}`} onClick={onEdit}><Pencil size={14} /></button>
            <button className="row-icon tone-danger" title="Sil" data-testid={`${kind}-delete-${r.id}`} onClick={onDelete}><Trash2 size={14} /></button>
          </>
        )}
      </td>
    </tr>
  );
}

const PRESETS = [["this-month", "Bu ay"], ["last-month", "Geçen ay"], ["this-year", "Bu yıl"], ["all", "Tümü"], ["custom", "Özel aralık"]];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function presetRange(p) {
  const t = new Date(`${today()}T12:00:00`);
  if (p === "this-month") return { from: ymd(new Date(t.getFullYear(), t.getMonth(), 1)), to: ymd(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
  if (p === "last-month") return { from: ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: ymd(new Date(t.getFullYear(), t.getMonth(), 0)) };
  if (p === "this-year") return { from: `${t.getFullYear()}-01-01`, to: `${t.getFullYear()}-12-31` };
  return { from: "", to: "" };
}

const HEADERS = {
  utts: ["Tarih", "Montaj kodu", "Plaka", "Kaydeden"],
  tracker: ["Tarih", "Plaka / Şase", "Müşteri", "Seri No'lar", "Açıklama"],
};

function InstallList({ kind, user, customers }) {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState(() => presetRange("this-month"));
  const [preset, setPreset] = useState("this-month");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const canManage = can(user, "manage_utts");
  const canAdd = canManage || can(user, "view_utts") || !!user?.work_only;

  const load = async () => {
    const params = new URLSearchParams();
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    if (status) params.set("invoiced", status);
    const { data } = await api.get(`/installs/${kind}?${params}`);
    setRows(data); setSel([]);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => setRows([])); }, [kind, range.from, range.to, status]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase().replace(/\s/g, "");
    if (!t) return rows;
    return rows.filter((r) => [r.plate, r.assembly_code, r.chassis_no, r.customer_name, r.description, ...(r.serial_nos || [])]
      .some((v) => String(v || "").toLowerCase().replace(/\s/g, "").includes(t)));
  }, [rows, q]);
  const invoicedCount = shown.filter((r) => r.invoiced).length;

  const mark = async (invoiced) => {
    let invoice_no = "";
    if (invoiced) invoice_no = window.prompt("Fatura numarası (opsiyonel):", "") ?? null;
    if (invoice_no === null) return;
    const { data } = await api.post(`/installs/${kind}/mark-invoiced`, { ids: sel, invoiced, invoice_no });
    setMsg(`${data.updated} kayıt ${invoiced ? "faturalandı" : "faturalanmadı"} olarak işaretlendi`);
    load();
  };
  const remove = async (r) => {
    if (!window.confirm("Bu montaj kaydı silinsin mi?")) return;
    await api.delete(`/installs/${kind}/${r.id}`);
    load();
  };
  const toggleAll = () => setSel(sel.length === shown.length ? [] : shown.map((r) => r.id));

  return (
    <Panel
      title={kind === "utts" ? "UTTS Montajları" : "Araç Takip Montajları"}
      subtitle={`${shown.length} kayıt · ${invoicedCount} faturalandı · ${shown.length - invoicedCount} bekliyor`}
      action={canAdd && <button className="primary-button" data-testid={`${kind}-new-button`} onClick={() => setEditing({})}><Plus size={15} /> Yeni montaj</button>}
    >
      {editing && (
        <InstallForm kind={kind} initial={editing.id ? editing : null} customers={customers}
                     onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      <div className="install-toolbar">
        <label className="field compact"><span>Dönem</span>
          <select data-testid={`${kind}-preset-filter`} value={preset}
                  onChange={(e) => { setPreset(e.target.value); if (e.target.value !== "custom") setRange(presetRange(e.target.value)); }}>
            {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="field compact"><span>Başlangıç</span>
          <input type="date" data-testid={`${kind}-date-from`} value={range.from} max={range.to || undefined}
                 onChange={(e) => { setPreset("custom"); setRange({ ...range, from: e.target.value }); }} />
        </label>
        <label className="field compact"><span>Bitiş</span>
          <input type="date" data-testid={`${kind}-date-to`} value={range.to} min={range.from || undefined}
                 onChange={(e) => { setPreset("custom"); setRange({ ...range, to: e.target.value }); }} />
        </label>
        <label className="field compact"><span>Durum</span>
          <select data-testid={`${kind}-status-filter`} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Tümü</option>
            <option value="false">Faturalanmadı</option>
            <option value="true">Faturalandı</option>
          </select>
        </label>
        <SearchInput testid={`${kind}-search`} value={q} onChange={setQ} placeholder="Plaka, kod, seri no, müşteri ara…" />
        {canManage && sel.length > 0 && (
          <div className="button-row" data-testid={`${kind}-bulk-bar`}>
            <strong>{sel.length} seçili</strong>
            <button className="primary-button" data-testid={`${kind}-mark-invoiced`} onClick={() => mark(true)}><CheckCircle2 size={15} /> Faturalandı işaretle</button>
            <button className="secondary-button" data-testid={`${kind}-mark-uninvoiced`} onClick={() => mark(false)}><RotateCcw size={15} /> Geri al</button>
          </div>
        )}
      </div>
      {msg && <div className="sync-msg" data-testid={`${kind}-msg`}>{msg}</div>}
      <div className="table-wrap" data-testid={`${kind}-table`}>
        <table>
          <thead>
            <tr>
              {canManage && <th><input type="checkbox" data-testid={`${kind}-select-all`} checked={!!shown.length && sel.length === shown.length} onChange={toggleAll} /></th>}
              {HEADERS[kind].map((h) => <th key={h}>{h}</th>)}
              <th>Durum</th><th></th>
            </tr>
          </thead>
          <tbody>
            {shown.length ? shown.map((r) => (
              <InstallRow key={r.id} kind={kind} r={r} canManage={canManage}
                          mine={r.created_by === user.id && localDay(r.created_at) >= localToday()}
                          selected={sel.includes(r.id)}
                          onToggle={() => setSel(sel.includes(r.id) ? sel.filter((x) => x !== r.id) : [...sel, r.id])}
                          onEdit={() => setEditing({ ...r, serial_nos: r.serial_nos?.length ? r.serial_nos : [""] })}
                          onDelete={() => remove(r)} />
            )) : (
              <tr><td colSpan={HEADERS[kind].length + 3} className="empty">Bu dönemde kayıt yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export function InstallsTabs({ user, customers }) {
  const [tab, setTab] = useState("utts");
  return (
    <>
      <div className="type-toggle install-tabs">
        <button type="button" data-testid="installs-tab-utts" className={tab === "utts" ? "active" : ""} onClick={() => setTab("utts")}>
          <Satellite size={15} /> UTTS Montajları
        </button>
        <button type="button" data-testid="installs-tab-tracker" className={tab === "tracker" ? "active" : ""} onClick={() => setTab("tracker")}>
          <Radio size={15} /> Araç Takip Montajları
        </button>
      </div>
      <InstallList key={tab} kind={tab} user={user} customers={customers} />
    </>
  );
}

export default function Installs({ user, customers }) {
  return (
    <>
      <Page eyebrow="MONTAJ TAKİBİ" title="UTTS ve Takip"
            subtitle="UTTS ve araç takip cihazı montajlarını kaydedin; ay sonunda toplu olarak faturalandı işaretleyin." />
      <InstallsTabs user={user} customers={customers} />
    </>
  );
}
